import { TSTitleCard } from '@/components/shared/card/Card';
import { PageControls, usePagination } from '@/components/shared/pagination/Pagination';
import tableStrings from '@/components/shared/table/table.translations';
import { FC } from 'react';
import { FiCalendar, FiCheck, FiEdit } from 'react-icons/fi';
import strings from '../admin.translations';
import { LeaveTypeRow } from './leaveTypes.types';

interface LeaveTypesListCardProps {
  leaveTypes: LeaveTypeRow[];
  onEdit: (leaveTypeId: string) => void;
  onAdd: () => void;
}

const columnWidths = {
  name: '50%',
  color: '17%',
  managerOnly: '17%',
  edit: '16%'
};
const pageSize = 50;

const LeaveTypeTableHeader: FC = () => (
  <thead>
    <tr>
      <th
        className="px-6 pb-2 pt-4 text-left text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.name }}
      >
        {strings.leaveTypeTable.name}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.color }}
      >
        {strings.leaveTypeTable.color}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.managerOnly }}
      >
        {strings.leaveTypeTable.managerOnly}
      </th>
      <th
        className="px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std"
        style={{ width: columnWidths.edit }}
      >
        {strings.leaveTypeTable.edit}
      </th>
    </tr>
  </thead>
);

interface LeaveTypeTableRowProps {
  leaveType: LeaveTypeRow;
  index: number;
  onEdit: (leaveTypeId: string) => void;
}

const LeaveTypeTableRow: FC<LeaveTypeTableRowProps> = ({ leaveType, index, onEdit }) => (
  <tr className={index % 2 === 0 ? 'bg-secondary-std' : 'bg-primary-std'}>
    <td className="px-4 py-4 text-sm font-medium text-primary-std sm:px-6" style={{ width: columnWidths.name }}>
      <div
        className={`flex h-[2.2rem] ${
          leaveType.active ? 'items-center' : 'flex-col justify-center'
        } ${!leaveType.active ? 'text-secondary-std' : ''}`}
      >
        <div>{leaveType.displayName.label || leaveType.name.label}</div>
        {!leaveType.active && <div className="font-semibold leading-tight">{strings.leaveTypeTable.disabled}</div>}
      </div>
    </td>
    <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.color }}>
      <div className="flex justify-center">
        <span className="h-6 w-6 rounded-full border border-primary-std" style={{ backgroundColor: leaveType.color }} />
      </div>
    </td>
    <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.managerOnly }}>
      <div className="flex justify-center">
        {leaveType.managerOnly ? <FiCheck className="h-5 w-5 text-green-600" /> : null}
      </div>
    </td>
    <td className="px-4 py-4 text-sm text-primary-std sm:px-6" style={{ width: columnWidths.edit }}>
      <div className="flex justify-center">
        <button
          type="button"
          aria-label={`${strings.leaveTypeTable.edit} ${leaveType.displayName.label || leaveType.name.label}`}
          className="text-secondary-std hover:text-primary-std"
          onClick={() => onEdit(leaveType.id)}
        >
          <FiEdit className="h-5 w-5" />
        </button>
      </div>
    </td>
  </tr>
);

export const LeaveTypesListCard: FC<LeaveTypesListCardProps> = ({ leaveTypes, onEdit, onAdd }) => {
  const [offset, limit, controls] = usePagination(pageSize, leaveTypes.length);
  const paginatedLeaveTypes = leaveTypes.slice(offset, offset + limit);

  return (
    <TSTitleCard
      title={strings.menu.leaveTypes}
      icon={FiCalendar}
      className="w-full"
      primaryAction={{ label: strings.leaveTypeTable.addNew, onClick: onAdd, icon: 'add' }}
    >
      <div className="w-full overflow-hidden">
        <table className="min-w-full table-fixed divide-y divide-primary-std border border-primary-std">
          <LeaveTypeTableHeader />
          <tbody className="divide-y divide-primary-std">
            {paginatedLeaveTypes.map((leaveType, index) => (
              <LeaveTypeTableRow key={leaveType.id} leaveType={leaveType} index={index} onEdit={onEdit} />
            ))}
          </tbody>
        </table>
      </div>
      {paginatedLeaveTypes.length > 0 ? (
        <PageControls {...controls} />
      ) : (
        <div className="flex w-full justify-center border-t border-primary-std px-6 py-5">
          <p className="py-1 text-xs font-medium uppercase text-primary-std">{tableStrings.nothingToShow}</p>
        </div>
      )}
    </TSTitleCard>
  );
};
